// Lista exaustiva de abreviações, títulos, patentes e numerações em EN, FR, ES, IT e PT
export const EXTENSIVE_ABBREVIATIONS = new Set([
  // Inglês (Títulos, honras, patentes militares, logradouros, medidas, tempo)
  'mr', 'mrs', 'ms', 'miss', 'dr', 'prof', 'sr', 'jr', 'rev', 'hon', 'esq',
  'st', 'ste', 'ave', 'rd', 'blvd', 'cres', 'ct', 'dept', 'univ', 'assn',
  'gen', 'col', 'maj', 'capt', 'lt', 'sgt', 'corp', 'pvt', 'adm', 'cmdr',
  'vol', 'vols', 'no', 'nos', 'fig', 'figs', 'p', 'pp', 'ed', 'eds',
  'vs', 'v', 'etc', 'viz', 'al', 'ibid', 'op', 'cit', 'ca', 'c',
  'jan', 'feb', 'mar', 'apr', 'jun', 'jul', 'aug', 'sep', 'sept', 'oct', 'nov', 'dec',
  'am', 'pm', 'a.m', 'p.m', 'bc', 'ad', 'b.c', 'a.d', 'ft', 'in', 'yd', 'mi',

  // Francês (Títulos, cortesias, eclesiásticos, ordens)
  'm', 'mme', 'mlle', 'mm', 'dr', 'prof', 'mgr', 'st', 'ste',
  'me', 'cie', 'soc', 'vol', 'chap', 'art', 'p', 'pp', 'n', 'no',
  'av', 'bd', 'rue', 'faub', 'janv', 'févr', 'avr', 'juill', 'sept', 'oct', 'nov', 'déc',

  // Espanhol (Tratamentos de cortesia, cargos e siglas comuns)
  'sr', 'sra', 'srta', 'dr', 'dra', 'prof', 'profa', 'd', 'dña',
  'ud', 'uds', 'vd', 'vds', 'ilmo', 'ilma', 'excmo', 'excma',
  'avda', 'pl', 'c', 'pza', 'cap', 'art', 'pág', 'págs', 'núm', 'num', 'gob',

  // Italiano (Títulos formais, nobiliárquicos e eclesiásticos)
  'sig', 'sig.ra', 'sig.na', 'dott', 'dott.ssa', 'prof', 'prof.ssa',
  'avv', 'ing', 'geom', 'rag', 'don', 'mons', 's', 'sant', 'fratelli',
  'f.lli', 'soc', 'cap', 'pag', 'pagg', 'art', 'v', 'n', 'via', 'p.za',

  // Português (Tratamentos, títulos acadêmicos e cartoriais)
  'sr', 'sra', 'srta', 'dr', 'dra', 'prof', 'profa', 'profª',
  'vmo', 'exmo', 'exma', 'ilmo', 'ilma', 'pe', 'padre', 'revmo',
  'av', 'r', 'al', 'trav', 'apto', 'bl', 'cap', 'pág', 'págs', 'art', 'nº', 'núm'
]);

// Divisão avançada via Regex com Lookbehind Negativo (evita quebra pós-abreviação ou pós-inicial)
function regexLookbehindSplit(text: string): string[] {
  return text
    .replace(/(?<!\b[A-Za-z]\.)(?<!\b(?:Mr|Mrs|Ms|Dr|Sr|Jr|Prof|St|Mme|Mlle|Av|Rd|no|vol)\.)([.?!]+[\s"»]+)(?=[A-Z"«\u00C0-\u017F])/g, "$1|SENTENCE_BOUNDARY|")
    .split("|SENTENCE_BOUNDARY|")
    .map(s => s.trim())
    .filter(Boolean);
}

export function splitBookIntoSentences(text: string, langCode: string = 'en'): string[] {
  if (!text || typeof text !== 'string') return [];

  const cleanText = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!cleanText) return [];

  const cleanLang = langCode.split('-')[0].toLowerCase();
  let candidateSentences: string[] = [];

  // 1. Camada A: Intl.Segmenter nativo do navegador
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    try {
      const segmenter = new (Intl as any).Segmenter(cleanLang, { granularity: 'sentence' });
      const raw = Array.from(segmenter.segment(cleanText), (s: any) => s.segment.trim()).filter(Boolean);
      candidateSentences = raw;
    } catch {
      candidateSentences = regexLookbehindSplit(cleanText);
    }
  } else {
    candidateSentences = regexLookbehindSplit(cleanText);
  }

  // 2. Camada B: Regex de segurança se o Intl.Segmenter falhou ou retornou bloco único
  if (candidateSentences.length <= 1 && cleanText.length > 150) {
    candidateSentences = regexLookbehindSplit(cleanText);
  }

  // 3. Camada C: Varredura de Fusão (Merge) de Abreviações, Iniciais e Fragmentos Órfãos
  const finalSentences: string[] = [];

  for (let i = 0; i < candidateSentences.length; i++) {
    let current = candidateSentences[i].trim();
    if (!current) continue;

    const words = current.split(/\s+/).filter(Boolean);
    const lastWordRaw = words[words.length - 1] || '';
    const lastWordClean = lastWordRaw.replace(/[^a-zA-ZáàâãéèêíïóôõöúçñÁÀÂÃÉÈÊÍÏÓÔÕÖÚÇÑ]/g, '').toLowerCase();

    // Condições de fragmento incompleto:
    const isSingleLetterInitial = /^[A-Za-zÁ-ú]\.$/.test(lastWordRaw);
    const isAbbreviation = EXTENSIVE_ABBREVIATIONS.has(lastWordClean) && current.endsWith('.');
    const isOrphan = current.length < 8 || words.length <= 1;

    // Se houver próximo bloco disponível e a frase atual for fragmento/abreviação:
    if ((isSingleLetterInitial || isAbbreviation || isOrphan) && i < candidateSentences.length - 1) {
      candidateSentences[i + 1] = current + ' ' + candidateSentences[i + 1].trim();
      continue;
    }

    finalSentences.push(current);
  }

  return finalSentences;
}
